import { useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { MasterDirectoryEntryType } from '@xing-com/crate-common-graphql-types';
import { useTracking } from '@xing-com/crate-communication-tracking';
import { useParams } from '@xing-com/crate-core-router';
import { getXingJobsDirectoryLetterPath } from '@xing-com/crate-core-routes';
import { Head } from '@xing-com/crate-master-directory-head';
import { useGetEntriesByLetter } from '@xing-com/crate-master-directory-hooks';
import {
  LinkList,
  MasterDirectoryDataBoundary,
} from '@xing-com/crate-master-directory-links';
import { DirectoryList } from '@xing-com/crate-master-directory-list';
import { SearchHeader } from '@xing-com/crate-master-directory-search-header';
import { useHost } from '@xing-com/crate-xinglet';

import * as S from './root.styled';

type Params = {
  letter: string;
};

type EntryListProps = {
  headline: React.ReactNode;
  letter: string;
  queryKey: number;
  showMore?: boolean;
  type: MasterDirectoryEntryType;
};

const EntryList: React.FC<EntryListProps> = ({
  headline,
  letter,
  queryKey,
  showMore,
  type,
}) => {
  const { collection } = useGetEntriesByLetter({
    letter,
    queryKey,
    type,
  });

  if (collection.length === 0) {
    return null;
  }

  return (
    <>
      <S.Headline size="xlarge">{headline}</S.Headline>
      <LinkList entries={collection} showMore={showMore} />
    </>
  );
};

export const MasterDirectoryRootPage: React.FC = () => {
  const { isPreview } = useHost();
  const externalUrl = isPreview
    ? 'https://preview.xing.com'
    : 'https://www.xing.com';
  const { formatMessage } = useIntl();
  const { letter } = useParams<Params>();
  const { track } = useTracking();
  const description = formatMessage(
    { id: 'CRATE_MASTER_DIRECTORY_DESCRIPTION' },
    { letter: letter.toUpperCase() }
  );
  const letterReplacement =
    letter === 'weitere'
      ? formatMessage({
          id: 'CRATE_MASTER_DIRECTORY_SPECIAL_CHARACTERS',
        })
      : letter.toUpperCase();
  const title = formatMessage(
    {
      id:
        letter === 'weitere'
          ? 'CRATE_MASTER_DIRECTORY_HEADLINE_OTHERS'
          : 'CRATE_MASTER_DIRECTORY_HEADLINE',
    },
    { letter: letterReplacement }
  );
  const path = getXingJobsDirectoryLetterPath({
    letter: letter.toLocaleLowerCase(),
  });
  const canonicalUrl = `${externalUrl}${path}`;
  useEffect(() => {
    track({
      application: 'wbm',
      eventSchema: 'basic',
      sentBy: 'directory',
      page: 'directory/overview',
      event: 'viewed_screen',
      type: 'nwt',
    });
  }, [track]);

  return (
    <>
      <Head
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        robots="noodp"
      />

      <SearchHeader />

      <S.Container>
        <S.Hero size="small" sizeConfined="large">
          <FormattedMessage
            id={
              letter === 'weitere'
                ? 'CRATE_MASTER_DIRECTORY_HEADLINE_OTHERS'
                : 'CRATE_MASTER_DIRECTORY_HEADLINE'
            }
            values={{
              letter: letterReplacement,
            }}
          />
        </S.Hero>

        <S.Headline size="xlarge">
          <FormattedMessage id="CRATE_MASTER_DIRECTORY_SEARCH_BY_LETTERS" />
        </S.Headline>
        <DirectoryList
          letter={letter}
          route={getXingJobsDirectoryLetterPath({ letter: '' })}
        />

        <S.Divider />

        <MasterDirectoryDataBoundary>
          {(queryKey) => (
            <EntryList
              headline={
                <FormattedMessage
                  id="CRATE_MASTER_DIRECTORY_JOBS_HEADLINE"
                  values={{
                    letter: letterReplacement,
                  }}
                />
              }
              letter={letter}
              queryKey={queryKey}
              showMore
              type={MasterDirectoryEntryType.JobRole}
            />
          )}
        </MasterDirectoryDataBoundary>

        {letter !== 'weitere' ? (
          <MasterDirectoryDataBoundary>
            {(queryKey) => (
              <EntryList
                headline={
                  <FormattedMessage
                    id="CRATE_MASTER_DIRECTORY_LOCATIONS_HEADLINE"
                    values={{
                      letter: letterReplacement,
                    }}
                  />
                }
                letter={letter}
                queryKey={queryKey}
                type={MasterDirectoryEntryType.Location}
              />
            )}
          </MasterDirectoryDataBoundary>
        ) : null}

        <MasterDirectoryDataBoundary>
          {(queryKey) => (
            <EntryList
              headline={
                <FormattedMessage
                  id="CRATE_MASTER_DIRECTORY_COMPANIES_HEADLINE"
                  values={{
                    letter: letterReplacement,
                  }}
                />
              }
              letter={letter}
              queryKey={queryKey}
              type={MasterDirectoryEntryType.Company}
            />
          )}
        </MasterDirectoryDataBoundary>
      </S.Container>
    </>
  );
};
